export const cleStockageJeton = "alpha-design-studio.alphaToolsBearer";

export function lireJetonStudio(): string | null {
  return sessionStorage.getItem(cleStockageJeton);
}

export function enregistrerJetonStudio(jeton: string): void {
  sessionStorage.setItem(cleStockageJeton, jeton);
}

export function supprimerJetonStudio(): void {
  sessionStorage.removeItem(cleStockageJeton);
}

export interface SessionStudio {
  utilisateur: {
    idUtilisateur: string;
    nomConnexion: string;
    nomAffiche: string;
    email: string;
    niveauAdmin: string;
  };
  organisations: Array<{
    idOrganisation: string;
    codeOrganisation: string;
    nomOrganisation: string;
  }>;
  codesApplication: string[];
  modeAcces: "admin_suite" | "application";
}

export interface ReponseConnexion {
  jetonAcces?: string;
  statut?: string;
  message?: string;
  mfaTicket?: string;
  session?: SessionStudio;
}

export interface EditeurToken {
  chemin: string;
  categorie: string;
  nom: string;
  valeur: string | Record<string, string>;
  surchargeApplicative: boolean;
}

export interface ReponseTokens {
  meta: Record<string, unknown>;
  tokens: Record<string, Record<string, unknown>>;
  surchargesAutorisees?: string[];
  editeurs: EditeurToken[];
}

export interface SurchargeToken {
  chemin: string;
  theme?: string;
  valeur: string;
}

export interface RapportSurcharge {
  erreurs: string[];
  avertissements: string[];
  tokens: ReponseTokens;
}

export interface ComposantCatalogue {
  nom: string;
  package: string;
  import: string;
  description: string;
  motsCles?: string[];
  reglagesVisuels?: ReglagesVisuelsComposantCatalogue;
}

export interface ReglagesVisuelsComposantCatalogue {
  alignement?: "gauche" | "centre" | "droite";
  arrondi?: number;
  couleurAccent?: string;
  couleurFond?: string;
  couleurTexte?: string;
  espace?: number;
  largeur?: "compacte" | "standard" | "large";
  tailleControle?: "compacte" | "standard" | "large";
  tailleTexte?: number;
}

export interface BrouillonStudio {
  id: string;
  nom: string;
  type: string;
  source: string;
  contenu: string;
  modifieLe: string;
}

export interface PromotionBrouillon {
  nom: string;
  package: "@alpha/ui";
  chemin: string;
  import: string;
  generationCatalogue: {
    commande: string;
    code: number;
    sortie: string;
    erreur: string;
  };
}

export interface SuppressionComposantStudio {
  nom: string;
  package: "@alpha/ui";
  chemin: string;
  generationCatalogue: {
    commande: string;
    code: number;
    sortie: string;
    erreur: string;
  };
}

export interface ProfilApparence {
  id: string;
  nom: string;
  description: string;
  famillePolice: string;
  tailleBase: number;
  tailleTitre: number;
  graisseTitre: number;
  styleCarte: "dense" | "equilibre" | "editorial";
  couleurPrincipale: string;
  couleurSurface: string;
  couleurTexte: string;
  couleurAccent: string;
  gradientDepart: string;
  gradientArrivee: string;
  angleDegres: number;
  imageUrl: string;
  arrondi: number;
  intensiteOmbre: number;
  modifieLe: string;
}

export interface ReponsePreproductionDesignSystem {
  statut: "pret" | "avertissement" | "bloque";
  titre: string;
  message: string;
  emplacement: {
    dossier: string;
    paquet: string;
    rapport: string;
    rapportValidation: string | null;
  };
  comparaison: {
    message: string;
    changements: number;
  };
  validation: {
    disponible: boolean;
    commande: string;
    code: number | null;
    sortie: string;
    erreur: string;
  };
  avertissements: string[];
  erreurs: string[];
}

export class ErreurApi extends Error {
  constructor(
    public readonly statut: number,
    public readonly code: string,
    message: string
  ) {
    super(message);
  }
}

export async function requeteApi<Reponse>(
  chemin: string,
  options: RequestInit = {}
): Promise<Reponse> {
  const jeton = lireJetonStudio();
  const entetes = new Headers(options.headers);
  entetes.set("content-type", "application/json");
  if (jeton) entetes.set("Authorization", `Bearer ${jeton}`);

  const reponse = await fetch(chemin, { ...options, headers: entetes });
  const texte = await reponse.text();
  const payload = texte ? (JSON.parse(texte) as unknown) : {};

  if (!reponse.ok) {
    const objet = lireObjet(payload);
    throw new ErreurApi(
      reponse.status,
      lireTexte(objet, "code") || "erreur_api",
      lireTexte(objet, "message") || "Erreur API."
    );
  }

  return payload as Reponse;
}

export async function envoyerChartePreproductionDesignSystem(
  paquet: unknown
): Promise<ReponsePreproductionDesignSystem> {
  return requeteApi<ReponsePreproductionDesignSystem>("/api/design-system/preproduction", {
    method: "POST",
    body: JSON.stringify({ paquet })
  });
}

function lireObjet(valeur: unknown): Record<string, unknown> {
  return typeof valeur === "object" && valeur !== null && !Array.isArray(valeur)
    ? (valeur as Record<string, unknown>)
    : {};
}

function lireTexte(valeur: Record<string, unknown>, cle: string): string {
  const entree = valeur[cle];
  return typeof entree === "string" ? entree : "";
}
